import type { Auth0ContextInterface } from '@auth0/auth0-react';
import { z } from 'zod';

const loginEmailSchema = z.string().trim().email();

/** Validated search parameters accepted by the login route. */
interface LoginSearch {
  /** Email address to prefill in Auth0 when it is valid. */
  email?: string;
}

/**
 * Extracts a valid invitation email from the login route search parameters.
 *
 * @param search - Untrusted search parameters supplied to the login route.
 * @returns A normalized login search object; invalid emails are omitted.
 */
export function parseLoginSearch(search: Record<string, unknown>): LoginSearch {
  const email = loginEmailSchema.safeParse(search.email);

  if (!email.success) return {};

  return { email: email.data };
}

/**
 * Starts Auth0 login and forwards a validated invitation email when available.
 *
 * @param loginWithRedirect - Auth0 SDK redirect function.
 * @param email - Optional validated email to prefill in Universal Login.
 * @returns The Auth0 redirect promise.
 */
export function startAuth0Login(
  loginWithRedirect: Auth0ContextInterface['loginWithRedirect'],
  email?: string
): Promise<void> {
  if (!email) return loginWithRedirect();

  return loginWithRedirect({
    authorizationParams: {
      login_hint: email,
    },
  });
}
